import { CurrencyFormatter } from '@utils/formatters/CurrencyFormatter';
import {
  ContentText,
  ContextTextContianer,
  GapContainer,
  LineContainer,
  PayButtonContainer,
  RootDiv,
} from '../styles/Resume.styles';
import {
  HorizontalLine,
  SkeletonTotalPriceText,
} from '../../paymentMethod/styles/PaymentFooter.styles';
import { PayButton } from './PayButton';
import { PaymentMethod } from '@interfaces';
import {
  LocalCancelButton,
  LocalTypographyButton,
} from '../styles/PayButton.styles';
import { usePermissions } from '@hooks/usePermissions';
import { useOrderState } from '@hooks/useOrderState';
import { useAuth } from '@hooks/useAuth';
import { useCurrentOrderLoadingStore } from '../zustand/currentOrderStore';

interface ResumeProps {
  totalCost: number;
  shippingCost: number;
  oldShippingCost: number;
  totalFinal: number;
  handlePay: () => void;
  paymentMethod: PaymentMethod;
  handleCancelPayOrderLocal: () => void;
}
export function Resume(props: ResumeProps) {
  const {
    totalCost,
    shippingCost,
    totalFinal,
    handlePay,
    paymentMethod,
    handleCancelPayOrderLocal,
    oldShippingCost,
  } = props;

  const { userCan } = usePermissions();
  const isAssistantUser = userCan('MANAGE_USER');
  const isLocalVendor = userCan('LOCAL_VENDOR');

  const { appUser: user } = useAuth();
  const { order } = useOrderState();

  const orderMetadata = order?.metadata;

  const userMekiCredits = user?.credits ?? 0;
  const { useMekiCredits, installments, insuranceInfo } = orderMetadata || {};

  const totalReimbursement = insuranceInfo?.totalReimbursement || 0;
  const totalDeductible = insuranceInfo?.totalDeductible || 0;

  const claimId = insuranceInfo?.claimId || null;

  const isPickUp = order?.deliveryType == 'PICK_UP' || false;

  const currencyFormatter = new CurrencyFormatter({
    fallback: '',
  });

  const subTotal = totalCost - shippingCost;
  const totalCopay = (insuranceInfo?.reimbursedProducts || []).reduce(
    (acc, product) => acc + product.unitCopay,
    0,
  );
  const sellDeductible = totalCopay - totalDeductible;

  const { loadingCurrentOrder } = useCurrentOrderLoadingStore();

  return (
    <RootDiv>
      <GapContainer>
        <ContextTextContianer>
          <ContentText bold>Subtotal productos</ContentText>
          {loadingCurrentOrder ? (
            <SkeletonTotalPriceText />
          ) : (
            <ContentText bold>{currencyFormatter.format(subTotal)}</ContentText>
          )}
        </ContextTextContianer>
        {Boolean(claimId) && (
          <>
            <LineContainer>
              <HorizontalLine />
            </LineContainer>
            <ContextTextContianer>
              <ContentText light>Reembolso póliza</ContentText>
              <ContentText light>
                {currencyFormatter.format(
                  totalReimbursement + totalDeductible,
                ) || '$0'}
              </ContentText>
            </ContextTextContianer>
            <ContextTextContianer>
              <ContentText light>Deducible póliza</ContentText>
              <ContentText light>
                {currencyFormatter.format(totalDeductible) || '$0'}
              </ContentText>
            </ContextTextContianer>
            <ContextTextContianer>
              <ContentText light>Aporte seguro</ContentText>
              <ContentText light>
                {currencyFormatter.format(totalReimbursement) || '$0'}
              </ContentText>
            </ContextTextContianer>
            <ContextTextContianer>
              <ContentText light>Deducible venta</ContentText>
              <ContentText light>
                {currencyFormatter.format(sellDeductible) || '$0'}
              </ContentText>
            </ContextTextContianer>
            <ContextTextContianer>
              <ContentText bold>Valor a pagar productos</ContentText>
              <ContentText bold>
                {currencyFormatter.format(subTotal - totalReimbursement) ||
                  '$0'}
              </ContentText>
            </ContextTextContianer>
          </>
        )}
        <LineContainer>
          <HorizontalLine />
        </LineContainer>
        {!isPickUp && (
          <ContextTextContianer>
            <ContentText light>Despacho</ContentText>
            {loadingCurrentOrder ? (
              <SkeletonTotalPriceText />
            ) : (
              <ContentText
                light={!oldShippingCost}
                highlight={oldShippingCost > 0}
                bold={oldShippingCost > 0}
              >
                {oldShippingCost ? (
                  <ContentText
                    light
                    highlight={false}
                    lineThrough
                    float="left"
                    marginRight={6}
                  >
                    {currencyFormatter.format(oldShippingCost)}
                  </ContentText>
                ) : null}
                {shippingCost > 0
                  ? currencyFormatter.format(shippingCost)
                  : 'Gratis'}
              </ContentText>
            )}
          </ContextTextContianer>
        )}
        {!isLocalVendor && useMekiCredits && (
          <ContextTextContianer>
            <ContentText light>Créditos Meki</ContentText>
            <ContentText>
              -
              {currencyFormatter.format(Math.min(totalCost, userMekiCredits)) ||
                '$0'}
            </ContentText>
          </ContextTextContianer>
        )}
        <ContextTextContianer>
          <ContentText bold>Total a pagar</ContentText>
          {loadingCurrentOrder ? (
            <SkeletonTotalPriceText />
          ) : (
            <ContentText bold>
              {totalFinal == 0 ? '$0' : currencyFormatter.format(totalFinal)}
            </ContentText>
          )}
        </ContextTextContianer>
      </GapContainer>
      {installments > 0 && (
        <>
          <LineContainer>
            <HorizontalLine />
          </LineContainer>
          <ContextTextContianer>
            <ContentText bold>Nº de cuotas</ContentText>
            <ContentText bold>{installments} cuotas</ContentText>
          </ContextTextContianer>
        </>
      )}
      <PayButtonContainer>
        <PayButton
          handleNext={handlePay}
          text={
            paymentMethod?.type == 'ONE_CLICK' ||
            isLocalVendor ||
            isAssistantUser ||
            paymentMethod?.type == 'MEKI_CREDIT' ||
            paymentMethod?.type == 'INSURANCE'
              ? 'Confirmar compra'
              : 'Ir a pagar'
          }
        />
      </PayButtonContainer>
      {isLocalVendor && (
        <LocalCancelButton onClick={handleCancelPayOrderLocal}>
          <LocalTypographyButton>Cancelar pago</LocalTypographyButton>
        </LocalCancelButton>
      )}
    </RootDiv>
  );
}
